import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "white" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-primary text-text-on-primary hover:bg-primary-hover active:bg-primary-press",
  secondary: "border border-border-strong text-text-primary hover:bg-surface-2",
  ghost: "bg-surface-2 text-text-primary hover:bg-surface-3",
  white: "bg-white text-bg hover:opacity-90",
  danger: "bg-danger text-white hover:opacity-90",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-7 gap-1.5 px-3 text-button",
  md: "h-9 gap-2 px-4 text-button",
  lg: "h-11 gap-2 px-6 text-body-lg",
};

const ICON_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "size-7 p-0",
  md: "size-9 p-0",
  lg: "size-11 p-0",
};

export function buttonClassName({
  variant = "primary",
  size = "md",
  iconOnly = false,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  className?: string;
} = {}): string {
  return cn(
    "relative inline-flex shrink-0 items-center justify-center rounded-sm font-medium " +
      "transition-colors focus-visible:outline-2 focus-visible:outline-primary " +
      "disabled:cursor-not-allowed disabled:opacity-50",
    VARIANT_CLASSES[variant],
    iconOnly ? ICON_SIZE_CLASSES[size] : SIZE_CLASSES[size],
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = "primary",
  size = "md",
  iconOnly = false,
  loading = false,
  leadingIcon,
  trailingIcon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps, ref) {
  return (
    <button
      ref={ref}
      className={buttonClassName({ variant, size, iconOnly, className })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      <span className={cn("inline-flex items-center gap-2", loading && "invisible")}>
        {leadingIcon}
        {children}
        {trailingIcon}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80" />
        </span>
      )}
    </button>
  );
});
